import { FCC } from "@/types/common";
import { Avatar, Stack, Tooltip, Typography } from "@mui/material";
import { deepOrange, deepPurple, blue, green, pink, amber } from "@mui/material/colors";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { InnerBox } from "@/components/elements/InnerBox";
import { divider } from "@/theme/colors";
import { IconCopyEmail } from "@/assets/icons/IconCopyEmail";
import { TGetPartnerDto } from "@/api/services/main/partner/dtos/get-partner.dto";
import { PartnerInfoItem } from "@/features/partner/components/partner-detail/PartnerInfoItem";
import { useEnum } from "@/api/services/global/enum";
import { findLabelByValue } from "@/utils/object";

type TPartnerContactInfoProps = {
  partner: TGetPartnerDto;
};

const colors = [deepOrange[500], deepPurple[500], blue[500], green[500], pink[500], amber[500]];
const getRandomColor = () => {
  const index = Math.floor(Math.random() * colors.length);
  return colors[index];
};

export const PartnerContactInfo: FCC<TPartnerContactInfoProps> = ({ partner }) => {
  const { partnerId } = useParams();
  const { data: contactMethodOptions } = useEnum("EPartnerContactMethod");
  const [bgColor] = useState<string>(() => {
    const savedColor = localStorage.getItem(`partnerBgColor_${partnerId}`);
    if (savedColor) return savedColor;
    const newColor = getRandomColor();
    localStorage.setItem(`partnerBgColor_${partnerId}`, newColor);
    return newColor;
  });
  const [messageTooltip, setMessageTooltip] = useState<string>("メールアドレスをコピー");

  const firstChar = partner.contactPerson?.charAt(0).toUpperCase();

  const copyEmail = async () => {
    setMessageTooltip("メールアドレスをコピーしました。");
    try {
      if (partner.email) {
        await navigator.clipboard.writeText(partner.email);
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <InnerBox title={<Typography variant="sub16Bold">担当者情報</Typography>} showDivider={false} borderColor={divider.high} sx={{ gap: "24px" }}>
      <Stack gap={2}>
        <Stack direction="row" alignItems="flex-start" gap={3}>
          <Stack direction="row" alignItems="center" gap={2} flex={1}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: bgColor, fontSize: "16px" }}>{firstChar}</Avatar>
            <PartnerInfoItem label="担当者名" value={partner.contactPerson} />
          </Stack>
          <PartnerInfoItem label="連絡方法" value={findLabelByValue(contactMethodOptions ?? [], partner.contactMethod)} />
        </Stack>
        <Stack direction="row" alignItems="flex-start" gap={3}>
          <PartnerInfoItem label="電話番号" value={partner.phoneNumber} showDivider={false} />
          <PartnerInfoItem
            label="メールアドレス"
            value={partner.email}
            showDivider={false}
            affixEnd={
              <Tooltip title={messageTooltip} placement="top-start" onClick={copyEmail}>
                <Typography>
                  <IconCopyEmail />
                </Typography>
              </Tooltip>
            }
          />
          <PartnerInfoItem label="その他の連絡先" value={partner.otherContacts} showDivider={false} />
        </Stack>
      </Stack>
    </InnerBox>
  );
};
